import {
  AnchorHTMLAttributes,
  DetailedHTMLProps,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { FontWeight, Text } from '@hhgtech/hhg-components/mantine'
import { Container, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useIntl } from 'react-intl'

import { useIsMobile } from 'hooks/useIsMobile'
import { assetImagePath, ENV } from 'interfaces/constants'
import {
  homepageCategories,
  topSectionSupported,
} from 'interfaces/constants/homepage'

import { StyledCategoryItems, StyledCarouselTag } from './HomeCategory.styled'

export type HomeCategoryProps = {
  className?: string
}

export type CategoryItemProps = {
  style?: React.CSSProperties
  className?: string
  item: {
    href: string
    icon?: string | JSX.Element
    title?: string
    id?: string
    defaultMessage?: string
    isNew?: boolean
    itemProps?: Omit<
      DetailedHTMLProps<
        AnchorHTMLAttributes<HTMLAnchorElement>,
        HTMLAnchorElement
      >,
      'href'
    >
  }
}

const CategoryItem: FC<CategoryItemProps> = ({ item, className, style }) => {
  const { formatMessage: f } = useIntl()
  return (
    <StyledCategoryItems
      href={item.href}
      {...item.itemProps}
      className={className}
      style={style}
    >
      <div className="hhg-icon">
        {item.icon ? (
          typeof item.icon === 'string' ? (
            <img
              loading="lazy"
              // src={`${assetImagePath}${item.icon}`}
              src={item.icon}
              alt={item.title}
            />
          ) : (
            item.icon
          )
        ) : null}
      </div>
      <span className="label line-clamp-2 max-sm:whitespace-pre-line !font-semibold md:max-w-[136.5px] md:!font-medium md:!text-[15px] md:!text-gray1000 md:!leading-6 md:!tracking-[0.3px]">
        {item.title || ''}
      </span>
      {item.isNew && (
        <Text className="new-tag" fw={FontWeight.halfBold}>
          {f({ id: 'common.new' })}
        </Text>
      )}
    </StyledCategoryItems>
  )
}

export const HomeCategory: FC<HomeCategoryProps> = ({ className }) => {
  const { formatMessage: f } = useIntl()

  const isMobile = useIsMobile()
  const theme = useMantineTheme()

  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`)

  const block = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(134)

  const categories = useMemo(
    () =>
      homepageCategories(
        ENV.NEXT_PUBLIC_DEPLOY_LOCALE as topSectionSupported,
      ).map((item) => ({
        ...item,
        title: f({
          id: item.id,
          defaultMessage: item.defaultMessage,
        }),
        icon: `${assetImagePath}${item.icon}`,
        itemProps: {
          'data-event-category': 'Recommended Topics',
          'data-event-action': 'Click',
          'data-event-label':
            typeof window === 'undefined'
              ? ''
              : `${window.location.origin}${item.href}`,
        },
      })),
    [],
  )

  useEffect(() => {
    if (block.current && isDesktop) {
      const el = block.current
      const widthContainer = el.offsetWidth
      const lenOfItem = categories.length
      const widthItem = (widthContainer - (lenOfItem - 1) * 8) / lenOfItem
      setWidth(widthItem)
    }
  }, [isDesktop])

  return (
    <Container className={className}>
      {isMobile ? (
        <div className="grid grid-cols-4 gap-y-6 gap-x-2 py-3">
          {categories.map((item, index) => (
            <CategoryItem key={`${index}`} item={item} />
          ))}
        </div>
      ) : (
        <div ref={block}>
          <StyledCarouselTag
            containScroll="trimSnaps"
            className={className}
            items={categories}
            sx={{
              '& .mantine-Carousel-controls': {
                display: isDesktop ? 'none' : 'auto',
              },
            }}
            renderItem={(item, index) => (
              <CategoryItem
                style={isDesktop ? { width: `${width}px` } : {}}
                key={index}
                item={item}
              />
            )}
          />
        </div>
      )}
    </Container>
  )
}
